import React from "react";
import { Box, Collapse, Typography, styled, useMediaQuery, useTheme } from "@mui/material";
import { ChevronDown, ChevronUp, Edit, Trash, Users, User, Boxes } from "lucide-react";

interface Profile {
  profile_id: string;
  backend_config?: {
    collections?: string[];
    prompts?: any[];
  };
}

interface Association {
  user_id: string;
  profile_id: string;
}

interface Group {
  groupname: string;
  description: string;
  users: { username: string }[];
}

type ItemType = "profile" | "association" | "group";

interface ItemListProps<T> {
  items: T[];
  isLoading: boolean;
  isDarkMode: boolean;
  itemsExpanded: boolean;
  setItemsExpanded: (expanded: boolean) => void;
  onDelete: (id: string) => Promise<void>;
  onEdit?: (item: T) => void;
  type: ItemType;
  title?: string;
  emptyMessage: string;
  loadingMessage: string;
  renderItem?: (item: T, index: number) => React.ReactNode;
  hideHeader?: boolean;
  children?: React.ReactNode;
  headerComponent?: React.ReactNode;
  totalCount?: number;
  showDeleteButton?: boolean;
}

const CollapseCard = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  backgroundColor: $isDark ? "#27272A" : "#F3F4F6",
  borderRadius: "8px",
  overflow: "hidden",
  marginBottom: "16px",
}));

const CollapseHeader = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 16px",
  cursor: "pointer",
  backgroundColor: $isDark ? "#27272A" : "#F3F4F6",
  color: $isDark ? "#FFFFFF" : "#1F2937",
  fontWeight: "bold",
  borderBottom: `1px solid ${$isDark ? "#3F3F46" : "#E5E7EB"}`,
  "&:hover": {
    backgroundColor: $isDark ? "#3F3F46" : "#E5E7EB",
  },
  "@media (max-width: 600px)": {
    fontSize: "12px",
  },
}));

const CollapseContent = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  padding: "16px",
  backgroundColor: $isDark ? "#27272A" : "#F3F4F6",
}));

const ItemListContainer = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  overflowY: "auto",
  maxHeight: "350px",
  padding: "16px",
  backgroundColor: $isDark ? "#18181B" : "#F9FAFB",
  borderRadius: "12px",
  marginTop: "24px",
  marginBottom: "16px",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: $isDark ? "#27272A" : "#F3F4F6",
    borderRadius: "0 8px 8px 0",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: $isDark ? "#3F3F46" : "#D1D5DB",
    borderRadius: "4px",
  },
}));

const ItemCard = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  backgroundColor: $isDark ? "#27272A" : "#F3F4F6",
  borderRadius: "8px",
  marginBottom: "8px",
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: $isDark ? "#3F3F46" : "#E5E7EB",
  },
  "@media (max-width: 768px)": {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "12px",
  },
}));

const ActionIconButton = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "36px",
  height: "36px",
  borderRadius: "6px",
  cursor: "pointer",
  padding: "8px",
  transition: "all 0.2s",
  backgroundColor: $isDark ? "#3F3F46" : "#E5E7EB",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const TruncatedText = styled(Typography)<{ $isDark?: boolean; $maxWidth?: string }>(({ $isDark, $maxWidth }) => ({
  color: $isDark ? "#FFFFFF" : "#1F2937",
  fontSize: "14px",
  fontWeight: "medium",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: $maxWidth || "200px",
  flexShrink: 0,
  "@media (max-width: 600px)": {
    fontSize: "12px",
    maxWidth: "120px",
  },
}));

const SecondaryText = styled(Typography)<{ $isDark?: boolean; $maxWidth?: string }>(({ $isDark, $maxWidth }) => ({
  color: $isDark ? "#A1A1AA" : "#6B7280",
  fontSize: "12px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: $maxWidth || "150px",
  flexShrink: 0,
  "@media (max-width: 600px)": {
    fontSize: "11px",
    maxWidth: "100px",
  },
}));

function isProfile(item: any): item is Profile {
  return 'profile_id' in item && (!('user_id' in item));
}

function isAssociation(item: any): item is Association {
  return 'user_id' in item && 'profile_id' in item;
}

function isGroup(item: any): item is Group {
  return 'groupname' in item && 'users' in item;
}

function CollapsibleItemsList<T>(props: ItemListProps<T>) {
    const {
    items, 
    isLoading, 
    isDarkMode, 
    itemsExpanded, 
    setItemsExpanded, 
    onDelete, 
    onEdit, 
    type, 
    title = "Items", 
    emptyMessage, 
    loadingMessage,
    renderItem,
    hideHeader = false,
    children,
    headerComponent,
    totalCount,
    showDeleteButton = true
  } = props;
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getIcon = () => {
    if (type === "profile") {
      return <User size={isMobile ? 16 : 20} color={isDarkMode ? "#71717A" : "#9CA3AF"} />;
    } else if (type === "group") {
      return <Boxes size={isMobile ? 16 : 20} color={isDarkMode ? "#71717A" : "#9CA3AF"} />;
    } else {
      return <Users size={isMobile ? 16 : 20} color={isDarkMode ? "#71717A" : "#9CA3AF"} />;
    }
  };

  const getPrimaryId = (item: any): string => {
    if (type === "profile" && isProfile(item)) {
      return item.profile_id;
    } else if (type === "group" && isGroup(item)) {
      return item.groupname;
    } else if (type === "association" && isAssociation(item)) {
      return item.user_id;
    }
    return "";
  };

  const getSecondaryInfo = (item: any): { text: string; count?: number } => {
    if (type === "profile" && isProfile(item)) {
      return {
        text: "colección(es)",
        count: item.backend_config?.collections?.length || 0
      };
    } else if (type === "group" && isGroup(item)) {
      return {
        text: item.description || "Sin descripción"
      };
    } else if (type === "association" && isAssociation(item)) {
      return {
        text: `Perfil: ${item.profile_id}`
      };
    }
    return { text: "" };
  };

  const renderDefaultItemContent = (item: any) => {
    const primaryId = getPrimaryId(item);
    const secondaryInfo = getSecondaryInfo(item);
    
    let displayText = primaryId;
    if (type === "association") {
      displayText = `Usuario: ${primaryId}`;
    }

    return (
      <Box sx={{ 
        display: "flex", 
        alignItems: "center",
        minWidth: 0, 
        flex: 1, 
        gap: 2
      }}>
        <Box sx={{ flexShrink: 0 }}>
          {getIcon()}
        </Box>
        
        <TruncatedText 
          $isDark={isDarkMode}
          $maxWidth={isMobile ? "120px" : "200px"}
          title={displayText}
        >
          {displayText}
        </TruncatedText>
        
        <SecondaryText
          $isDark={isDarkMode}
          $maxWidth={isMobile ? "100px" : "150px"}
          title={secondaryInfo.count !== undefined ? `${secondaryInfo.count} ${secondaryInfo.text}` : secondaryInfo.text}
        >
          {secondaryInfo.count !== undefined ? `${secondaryInfo.count} ${secondaryInfo.text}` : secondaryInfo.text}
        </SecondaryText>
      </Box>
    );
  };

  const getItemKey = (item: any): string => {
    if (type === "profile" && isProfile(item)) {
      return item.profile_id;
    } else if (type === "group" && isGroup(item)) {
      return item.groupname;
    } else if (type === "association" && isAssociation(item)) {
      return `${item.user_id}-${item.profile_id}`;
    }
    return Math.random().toString();
  };

  const renderCountBadge = () => {
    const count = totalCount !== undefined ? totalCount : items.length;
    return (
      <div className="bg-[#1E2F25] text-[#1BCF66] border border-[#1BCF66] rounded-full w-7 h-7 flex items-center justify-center ml-2">
        {count}
      </div>
    );
  };

  return (
    <Box sx={{ mt: 4 }}>
      <CollapseCard $isDark={isDarkMode}>
        <CollapseHeader
          $isDark={isDarkMode}
          onClick={() => setItemsExpanded(!itemsExpanded)}
        >
          {headerComponent ? (
            headerComponent
          ) : (
            <div className="flex items-center">
              <Typography 
                sx={{ 
                  fontSize: isMobile ? "14px" : "16px", 
                  fontWeight: "bold",
                  color: title === "Grupos Existentes" || title === "Asociaciones Existentes" ? "#1BCF66" : (isDarkMode ? "#FFFFFF" : "#1F2937") 
                }}
              >
                {hideHeader ? "" : title}
              </Typography>
              
              {(title === "Grupos Existentes" || title === "Asociaciones Existentes") && renderCountBadge()}
            </div>
          )}
          {itemsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </CollapseHeader>
        <Collapse in={itemsExpanded}>
          <CollapseContent $isDark={isDarkMode} sx={{ p: 0 }}>
            <ItemListContainer $isDark={isDarkMode} sx={{ m: 0, borderRadius: 0 }}>
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                    color: isDarkMode ? "#71717A" : "#9CA3AF",
                    fontSize: isMobile ? "12px" : "14px"
                  }}
                >
                  {loadingMessage}
                </Box>
              ) : items.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                    color: isDarkMode ? "#71717A" : "#9CA3AF",
                    fontSize: isMobile ? "12px" : "14px"
                  }}
                >
                  {emptyMessage}
                </Box>
              ) : (
                items.map((item, index) => (
                  <ItemCard key={getItemKey(item)} $isDark={isDarkMode}>
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                      flex: 1,
                      "@media (max-width: 768px)": {
                        width: "100%",
                      },
                    }}>
                      {renderItem ? renderItem(item, index) : renderDefaultItemContent(item)}
                    </Box>                    
                    <Box sx={{ 
                      display: "flex", 
                      gap: 1, 
                      flexShrink: 0,
                      "@media (max-width: 768px)": {
                        justifyContent: "flex-start",
                        width: "100%",
                      },
                    }}>
                      {onEdit && (type === "profile" || type === "group") && (
                        <ActionIconButton
                          $isDark={isDarkMode}
                          onClick={() => onEdit && onEdit(item)}
                          sx={{
                            "&:hover": {
                              backgroundColor: isDarkMode ? "#4ADE80" : "#22C55E",
                              "& svg": {
                                color: "#FFFFFF !important"
                              }
                            }
                          }}
                        >
                          <Edit size={16} color={isDarkMode ? "#FFFFFF" : "#1F2937"} />
                        </ActionIconButton>
                      )}
                      {showDeleteButton && (
                        <ActionIconButton
                          $isDark={isDarkMode}
                          onClick={() => onDelete(getPrimaryId(item))}
                          sx={{
                            backgroundColor: "#EF4444", 
                            "&:hover": {
                              backgroundColor: "#DC2626", 
                              "& svg": {
                                color: "#FFFFFF !important"
                              }
                            }
                          }}
                        >
                          <Trash size={16} color="#FFFFFF" />
                        </ActionIconButton>
                      )}
                    </Box>
                  </ItemCard>
                ))
              )}
            </ItemListContainer>
          </CollapseContent>
        </Collapse>
      </CollapseCard>
    </Box>
  );
}

export default CollapsibleItemsList;